import { useEffect, useState, useCallback } from "react";
import { Loader2, Send, ExternalLink, Eye, Download, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const ENGAGEMENT_STYLES = {
  hot: { label: "Downloaded", bg: "bg-emerald-100", text: "text-emerald-700", icon: Download },
  warm: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700", icon: Eye },
  cold: { label: "Not opened", bg: "bg-slate-100", text: "text-slate-600", icon: Clock },
  stale: { label: "Stale (7d+)", bg: "bg-rose-100", text: "text-rose-700", icon: Clock },
};

/**
 * Dashboard widget showing the borrower's recent active lender-package share
 * links + their engagement status. Lets them fire a one-click "Just checking
 * in" follow-up email straight from the row.
 *
 * Pro/Enterprise only — silently hides itself for free users (the pipeline
 * endpoint 402s for them, so we just don't render).
 */
export default function LenderPipelineWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [followUpLink, setFollowUpLink] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lender-package/pipeline?limit=8");
      setData(r.data);
    } catch (err) {
      // 402 (free user) or 401 — hide the widget silently.
      if (err.response?.status === 402 || err.response?.status === 401) {
        setForbidden(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (forbidden) return null;
  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-center justify-center h-32 text-slate-400" data-testid="lender-pipeline-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading lender pipeline…
      </div>
    );
  }
  if (!data || !data.items?.length) return null;  // hide when borrower has no active links

  const { items, summary } = data;

  return (
    <>
      <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="lender-pipeline-widget">
        <header className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-slate-700">Lender pipeline</div>
            <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
              Active share links
            </h2>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono">
            {summary.hot > 0 && (
              <span className="text-emerald-700" title="Lenders who downloaded the package">
                {summary.hot} hot
              </span>
            )}
            {summary.warm > 0 && (
              <span className="text-amber-700" title="Lenders who viewed but didn't download">
                {summary.warm} warm
              </span>
            )}
            {summary.stale > 0 && (
              <span className="text-rose-700" title="No activity in 7+ days">
                {summary.stale} stale
              </span>
            )}
          </div>
        </header>

        <ul className="divide-y divide-slate-100" data-testid="lender-pipeline-rows">
          {items.map((row) => {
            const eng = ENGAGEMENT_STYLES[row.engagement] || ENGAGEMENT_STYLES.cold;
            const Icon = eng.icon;
            const lastIso = row.latest_view?.viewed_at || row.created_at;
            return (
              <li key={row.id} className="py-3 flex items-center gap-3" data-testid={`lender-pipeline-row-${row.id}`}>
                <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${eng.bg} ${eng.text}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {row.recipient_label || row.label || "Untitled link"}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded-sm ${eng.bg} ${eng.text} font-mono uppercase tracking-widest text-[9px]`}>
                      {eng.label}
                    </span>
                    {row.view_count > 0 && (
                      <span className="tabular-nums">
                        {row.view_count} view{row.view_count === 1 ? "" : "s"}
                      </span>
                    )}
                    {row.download_count > 0 && (
                      <span className="tabular-nums">
                        · {row.download_count} dl
                      </span>
                    )}
                    <span className="text-slate-400">· {fmtDate(lastIso)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {row.url && (
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-slate-400 hover:text-slate-700 transition"
                      title="Open public link"
                      data-testid={`lp-pipeline-${row.id}-open`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setFollowUpLink(row)}
                    className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 px-2 py-1 border border-emerald-200 hover:border-emerald-400 rounded-sm transition"
                    data-testid={`lp-pipeline-${row.id}-followup`}
                  >
                    <Send className="w-3 h-3 inline -mt-0.5 mr-1" />
                    Follow up
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <FollowUpDialog
        link={followUpLink}
        onOpenChange={(o) => !o && setFollowUpLink(null)}
        onSent={() => { setFollowUpLink(null); load(); }}
      />
    </>
  );
}

function FollowUpDialog({ link, onOpenChange, onSent }) {
  const open = !!link;
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setTo("");
      setMessage("");
      setSending(false);
    }
  }, [open]);

  const send = async (e) => {
    e?.preventDefault();
    const recipients = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      toast.error("Enter at least one recipient");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/lender-package/shares/${link.id}/follow-up`, {
        to: recipients,
        message: message || null,
      });
      if (r.data?.ok) {
        toast.success(`Follow-up sent to ${recipients.join(", ")}`);
        onSent?.();
      } else if (r.data?.result?.status === "mocked") {
        toast.success(`Mock-sent (configure SMTP/Resend to actually deliver)`);
        onSent?.();
      } else {
        toast.error(`Send failed: ${r.data?.result?.error || "configure SMTP/Resend in Settings"}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!link) return null;
  const recipient = link.recipient_label || link.label || "your lender";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lp-pipeline-followup-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" /> Follow up with {recipient}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Re-emails the existing share link with a "just checking in" template. The original
            password (if any) is unchanged.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <label className="block">
            <div className="overline text-slate-600 mb-1">Recipient(s)</div>
            <input
              type="text" required
              className="input-clean w-full"
              value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="loan-officer@bank.com"
              data-testid="lp-pipeline-followup-to"
              autoFocus
            />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">Add a personal note (optional)</div>
            <textarea
              className="input-clean w-full min-h-[80px]"
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g., 'No rush, but wanted to make sure this didn't get lost...'"
              maxLength={1000}
              data-testid="lp-pipeline-followup-message"
            />
          </label>
        </form>
        <DialogFooter className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-outline-dark inline-flex items-center gap-1 px-3"
            data-testid="lp-pipeline-followup-cancel"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="lp-pipeline-followup-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send follow-up
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
